import styled from '@emotion/styled';
import {Col, media, Row} from '@nfq/react-grid';

import {PayloadImage} from 'UI/components/layout/PayloadImage';
import {PayloadLexicalSmall} from 'UI/components/layout/PayloadLexicalSmall';

import {transient} from 'Application/configs/emotionTransientProps';

import type {LexicalContent} from '@nfq/payload-lexical-parser';
import type {Media} from 'types/payload-types';

/**
 * The props for the `FaqBody` component, which renders the body of an FAQ entry.
 * This interface defines the structure of the answer content, optional image, and
 * testing attributes. It ensures that the FAQ body can be displayed with or without
 * an accompanying image while maintaining accessibility and testability.
 */
interface ComponentProps {
    /**
     * The main answer text for the FAQ entry.
     * Rendered using `PayloadLexical` to support rich text formatting.
     */
    answer: LexicalContent;
    /**
     * A flag indicating whether the FAQ entry should include an image.
     * Defaults to `false` if not provided.
     */
    hasImage?: boolean | null;
    /**
     * The optional image associated with the FAQ entry.
     * Can be a `Media` object or a string reference (e.g., a URL).
     */
    image?: Media | string | null;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'FaqBody'
     */
    testId?: string;
}

/**
 * A component that renders the body of an FAQ entry with an answer and optional image.
 * The `FaqBody` component displays the answer text inside a styled wrapper, and if
 * `hasImage` is true, it renders an accompanying image in a separate column. The layout
 * adapts responsively: when an image is present, the answer and image each take half the width
 * on large screens, while without an image, the answer takes the full width.
 * The `testId` prop is applied as a `data-cy` attribute to facilitate reliable end-to-end testing.
 *
 * @param props          The component props.
 * @param props.answer   The answer text content, rendered via `PayloadLexical`.
 * @param props.hasImage A flag indicating whether an image should be displayed. Defaults to `false`.
 * @param props.image    The optional image associated with the FAQ entry.
 * @param props.testId   An optional identifier used for testing, applied as `data-cy`. Defaults to `'FaqBody'`.
 * @returns A JSX element rendering the FAQ answer section, optionally with an image.
 *
 * @example
 * ```tsx
 * <FaqBody
 *   answer={faqAnswer}
 *   hasImage={true}
 *   image={faqImage}
 * />
 * ```
 */
const FaqBody = ({answer, hasImage = false, image, testId = 'FaqBody'}: ComponentProps) => (
    <Row align="stretch" testId={testId}>
        {/* eslint-disable-next-line @nfq/no-magic-numbers */}
        <Col lg={hasImage ? 6 : 8} xs={12}>
            <ContentWrapper>
                {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
                {answer && <PayloadLexicalSmall description={answer} />}
            </ContentWrapper>
        </Col>
        {hasImage && (
            <StyledCol align="center" justify="center" lg={6} xs={12}>
                <ImageWrapper>
                    <PayloadImage
                        expectedSizes={{
                            xs: 272, sm: 500, md: 500, lg: 464, xl: 575, xxl: 684
                        }}
                        image={image}
                    />
                </ImageWrapper>
            </StyledCol>
        )}
    </Row>
);

FaqBody.displayName = 'FaqBody';

export {FaqBody};

const ContentWrapper = styled('div', transient)`
    display: flex;
    flex-direction: column;
    height: 100%;
`;

const StyledCol = styled(Col, transient)`
    max-height: 100%;
`;

const ImageWrapper = styled('div', transient)`
    border-radius: 24px;
    max-width: 50rem;
    overflow: hidden;
    width: 100%;

    ${media('lg')} {
        aspect-ratio: initial;
        display: flex;
        flex-direction: column;
        height: 100%;
        max-height: 100%;
        max-width: initial;
        min-height: 0;
        min-width: 0;
    }
`;